import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOffersForRequest, OfferStatus, useFarmerExpressOffersStore } from '../services';
import { Offer, Request } from '../types';
import { OffersList } from './OffersList';
import { RequestCard } from './RequestCard';

interface RequestsListProps {
	requests: Request[];
	selectedRequestId: string | null;
	isLoading: boolean;
	error: string | null;
	onSelectRequest: (requestId: string) => void;
	// Mobile layout props (optional)
	isMobile?: boolean;
	offers?: Offer[];
	isOffersLoading?: boolean;
	offersError?: string | null;
	requestStatus?: OfferStatus;
	onRefreshOffers?: () => void; // Add callback to refresh offers data
}

export const RequestsList: React.FC<RequestsListProps> = ({
	requests,
	selectedRequestId,
	isLoading,
	error,
	onSelectRequest,
	// Mobile layout props
	isMobile,
	offers: globalOffers,
	isOffersLoading: globalIsOffersLoading,
	offersError: globalOffersError,
	requestStatus = 'active',
	onRefreshOffers,
}) => {
	const { t } = useTranslation();
	const { setOffers, setIsOffersLoading, setOffersError } = useFarmerExpressOffersStore();
	const [expandedRequestIds, setExpandedRequestIds] = useState<string[]>([]);
	const [selectedServiceIds, setSelectedServiceIds] = useState<Record<string, string>>({});

	// Track offers for each request ID in mobile view
	const [requestOffers, setRequestOffers] = useState<Record<string, Offer[]>>({});
	const [requestOffersLoading, setRequestOffersLoading] = useState<Record<string, boolean>>({});
	const [requestOffersError, setRequestOffersError] = useState<Record<string, string | null>>({});

	// Handle request card click
	const handleRequestClick = useCallback(
		async (requestId: string, serviceId: string) => {
			// Store the selected service ID for this request
			setSelectedServiceIds(prev => ({
				...prev,
				[requestId]: serviceId
			}));

			// For mobile, handle expand/collapse functionality
			if (isMobile) {
				setExpandedRequestIds((prev) => {
					// If already expanded, collapse it and don't fetch data
					if (prev.includes(requestId)) {
						return prev.filter((id) => id !== requestId);
					} else {
						fetchOffersForRequest(requestId, serviceId);
						return [...prev, requestId];
					}
				});
			}

			// Only call onSelectRequest after state updates in non-mobile mode
			// or when not handling expand/collapse
			if (!isMobile) {
				// First trigger the parent component callback to update selection
				onSelectRequest(requestId);
				// Only fetch offers globally if not mobile
				fetchGlobalOffers(requestId, serviceId);
			}
		},
		[onSelectRequest, isMobile]
	);

	// Fetch offers globally (for desktop view)
	const fetchGlobalOffers = async (requestId: string, serviceId: string) => {
		try {
			setIsOffersLoading(true);
			const response = await getOffersForRequest(requestId, serviceId);

			setOffers(response.data.offers);
		} catch (error) {
			console.error(`[RequestsList] Error directly fetching offers for request ID: ${requestId}`, error);
			setOffersError(error instanceof Error ? error.message : 'Error fetching offers');
		} finally {
			setIsOffersLoading(false);
		}
	};

	// Fetch offers for a specific request (for mobile view)
	const fetchOffersForRequest = async (requestId: string, serviceId: string) => {
		try {
			// Set loading state for this specific request
			setRequestOffersLoading((prev) => ({ ...prev, [requestId]: true }));
			setRequestOffersError((prev) => ({ ...prev, [requestId]: null }));

			const response = await getOffersForRequest(requestId, serviceId);

			// Save offers for this specific request
			setRequestOffers((prev) => ({
				...prev,
				[requestId]: response.data.offers,
			}));
		} catch (error) {
			console.error(`[RequestsList] Mobile: Error fetching offers for request ID: ${requestId}`, error);
			setRequestOffersError((prev) => ({
				...prev,
				[requestId]: error instanceof Error ? error.message : 'Error fetching offers',
			}));
		} finally {
			setRequestOffersLoading((prev) => ({ ...prev, [requestId]: false }));
		}
	};

	// Show loading state
	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
				<CircularProgress size={30} sx={{ color: '#3da08f' }} />
			</Box>
		);
	}

	// Show error state
	if (error) {
		return (
			<Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
				<Typography variant="body1">{error}</Typography>
			</Box>
		);
	}

	// Show empty state
	if (!requests || requests.length === 0) {
		return (
			<Box sx={{ textAlign: 'center', py: 4 }}>
				<Typography variant="body1">{t('noRequests')}</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{/* Requests list */}
			{requests.map((request, index) => {
				// Use request_id consistently for tracking expanded state
				const requestId = request.request_id || request.id;
				const serviceId = request.services_id || request.service_id || '';
				const isExpanded = expandedRequestIds.includes(requestId);
				const isSelected = isExpanded || selectedRequestId === requestId;
				const userId = request.specialist_id;
				
				// Determine if this card should have a different background based on service_id
				const selectedServiceId = selectedServiceIds[requestId];
				const isServiceSelected = selectedServiceId === serviceId;
				
				// Combine selection states
				const cardIsSelected = isSelected && (!selectedServiceId || isServiceSelected);

				return (
					<React.Fragment key={`${request.id}_${index}`}>
						<RequestCard
							request={request}
							isSelected={cardIsSelected}
							onClick={handleRequestClick}
							isMobile={isMobile}
						/>

						{/* For mobile, render offers list below the expanded request */}
						{isMobile && isExpanded && (
							<Box sx={{ pl: 2, pr: 2, mb: 3 }}>
								{requestOffersLoading[requestId] ? (
									<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
										<CircularProgress size={30} sx={{ color: '#3da08f' }} />
									</Box>
								) : (
									<OffersList
										offers={requestOffers[requestId] || []}
										requestId={requestId}
										userId={userId}
										isLoading={false}
										error={requestOffersError[requestId] || null}
										requestStatus={requestStatus}
										onRefreshOffers={onRefreshOffers}
									/>
								)}
							</Box>
						)}
					</React.Fragment>
				);
			})}
		</Box>
	);
};
